import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  MutableRefObject,
  ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';

import { Sticky } from '@travel/ui';
import { cx } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';
import useDivElementHeight from 'hooks/useDivElementHeight';
import { setSelectedNavSection } from 'store/providerInformation/actions';

import { isClient } from 'store/utils';

import styles from './scrollingNavigation.module.scss';

export type Section = {
  id: string;
  title: string;
  target: MutableRefObject<HTMLDivElement | null>;
};

type Props = {
  sections: Section[];
  selectedSection?: string;
  otherChildren?: ReactNode;
};

function ScrollingNavigation(props: Props) {
  const { sections, selectedSection, otherChildren } = props;
  const navSections = sections.filter(section => section);

  const [active, setActive] = useState<string>(selectedSection || navSections[0].id);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentHeight = useDivElementHeight(contentRef);

  const dispatch = useDispatch();

  const handleScroll = useCallback(() => {
    navSections.forEach(({ id, target }, index) => {
      const sectionRect = target.current?.getBoundingClientRect();

      const inViewThreshold = contentHeight + 60; // Can be regarded as user enters this section when scrolling the screen to where the section is 60px under the navbar.
      if (
        sectionRect &&
        sectionRect.top <= inViewThreshold &&
        sectionRect.bottom > inViewThreshold
      ) {
        setActive(id);
        dispatch(setSelectedNavSection(navSections[index]));
      }
    });
  }, [contentHeight, dispatch, navSections]);

  useEffect(() => {
    isClient && window.addEventListener('scroll', handleScroll);

    return () => {
      isClient && window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleNavItemClick = (id: string) => () => scrollToTarget(id);

  const scrollToTarget = useCallback(
    (sectionId: string) => {
      const offsetTop =
        navSections.find(({ id }) => id === sectionId)?.target.current?.offsetTop ?? 0;
      isClient &&
        window.scrollTo({
          top: offsetTop - contentHeight,
          behavior: 'smooth',
        });
    },
    [contentHeight, navSections],
  );

  return (
    <Sticky
      position="top"
      floatingStateClassName={styles.floating}
      className={styles.wrapper}
      rootMargin={`-${contentHeight}px 0px 0px 0px`}
      childrenHeight={contentHeight}
    >
      <div
        className={styles.content}
        ref={contentRef}
        data-testid="scrollingNavigationBar-container"
      >
        <ul className={styles.navItemList}>
          {navSections.map(({ id, title }) => (
            <li
              className={cx(styles.navItem, id === active && styles.selectedItem)}
              key={id}
              data-testid={`scrollingNavigationBar-item-${id}`}
              data-selected={id === active}
            >
              <ButtonNoStyle onClick={handleNavItemClick(id)}>{title}</ButtonNoStyle>
            </li>
          ))}
        </ul>
        {otherChildren}
      </div>
    </Sticky>
  );
}

export default ScrollingNavigation;
